import { Drawer as BaseDrawer } from '@base-ui/react/drawer';
import clsx from 'clsx';
import { useState } from 'react';
import { useIsomorphicLayoutEffect } from '@_/hooks';

export type DrawerDirection = 'bottom' | 'left' | 'right' | 'top';
export type DrawerSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

const horizontalSizes: Record<DrawerSize, string> = {
  sm: 'w-80',
  md: 'w-[28rem]',
  lg: 'w-[42rem]',
  xl: 'w-6xl',
  full: 'w-full'
};

const verticalSizes: Record<DrawerSize, string> = {
  sm: 'h-80',
  md: 'h-[28rem]',
  lg: 'h-[42rem]',
  xl: 'h-[72rem]',
  full: 'h-full'
};

const swipeDirections = {
  bottom: 'down',
  left: 'left',
  right: 'right',
  top: 'up'
} as const;

export interface DrawerProps extends React.PropsWithChildren {
  className?: string;
  dark?: boolean;
  direction?: DrawerDirection;
  disablePointerDismissal?: boolean;
  disableSwipeDismissal?: boolean;
  onAnimationEnd?: () => void;
  onOpenChange: (open: boolean) => void;
  open?: boolean;
  size?: DrawerSize;
}

export function Drawer({
  children,
  className,
  dark,
  direction = 'right',
  disablePointerDismissal = false,
  disableSwipeDismissal = false,
  onAnimationEnd,
  onOpenChange,
  open = false,
  size = 'md'
}: DrawerProps) {
  const [rootOpen, setRootOpen] = useState(false);

  // Consumers can mount with `open=true`. Start closed, then open before paint
  // so Base UI detects the transition and applies `data-starting-style`.
  useIsomorphicLayoutEffect(() => {
    setRootOpen(open);
  }, [open]);

  const horizontal = direction === 'left' || direction === 'right';

  return (
    <BaseDrawer.Root
      disablePointerDismissal={disablePointerDismissal}
      onOpenChange={onOpenChange}
      onOpenChangeComplete={onAnimationEnd ? () => onAnimationEnd() : undefined}
      open={rootOpen}
      swipeDirection={swipeDirections[direction]}
    >
      <BaseDrawer.Portal>
        <BaseDrawer.Backdrop
          className={clsx(
            'fixed inset-0 z-40 bg-black/20',
            'opacity-[calc(1-var(--drawer-swipe-progress))] transition-opacity duration-300 ease-out',
            'data-ending-style:opacity-0 data-ending-style:duration-200 data-ending-style:ease-in data-starting-style:opacity-0',
            'data-swiping:duration-0 motion-reduce:transition-none'
          )}
        />

        <BaseDrawer.Viewport className="pointer-events-none fixed inset-0 z-40">
          <BaseDrawer.Popup
            data-mode={dark ? 'dark' : undefined}
            className={clsx(
              'pointer-events-auto fixed max-h-full max-w-full bg-white shadow-xl outline-none dark:bg-gray-950 dark:text-white',
              'transition-transform duration-350 ease-panel data-ending-style:duration-200 data-ending-style:ease-in data-swiping:duration-0 data-swiping:select-none motion-reduce:transition-none',

              // Cover the viewport edge while Base UI rubber-bands past the fully open position.
              'after:pointer-events-none after:absolute after:block after:bg-inherit',

              horizontal ? horizontalSizes[size] : verticalSizes[size],

              direction === 'left' && [
                'inset-y-0 left-0 transform-[translateX(var(--drawer-swipe-movement-x))]',
                'data-ending-style:transform-[translateX(-100%)] data-starting-style:transform-[translateX(-100%)]',
                'after:inset-y-0 after:right-full after:w-12'
              ],
              direction === 'right' && [
                'inset-y-0 right-0 transform-[translateX(var(--drawer-swipe-movement-x))]',
                'data-ending-style:transform-[translateX(100%)] data-starting-style:transform-[translateX(100%)]',
                'after:inset-y-0 after:left-full after:w-12'
              ],
              direction === 'top' && [
                'inset-x-0 top-0 transform-[translateY(var(--drawer-swipe-movement-y))]',
                'data-ending-style:transform-[translateY(-100%)] data-starting-style:transform-[translateY(-100%)]',
                'after:inset-x-0 after:bottom-full after:h-12'
              ],
              direction === 'bottom' && [
                'inset-x-0 bottom-0 transform-[translateY(var(--drawer-swipe-movement-y))]',
                'data-ending-style:transform-[translateY(100%)] data-starting-style:transform-[translateY(100%)]',
                'after:inset-x-0 after:top-full after:h-12'
              ],
              className
            )}
          >
            <BaseDrawer.Content
              className="h-full w-full overflow-hidden rounded-[inherit]"
              data-base-ui-swipe-ignore={disableSwipeDismissal ? '' : undefined}
            >
              {children}
            </BaseDrawer.Content>
          </BaseDrawer.Popup>
        </BaseDrawer.Viewport>
      </BaseDrawer.Portal>
    </BaseDrawer.Root>
  );
}
